import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { api, BACKEND_URL } from "./api";

const SettingsContext = createContext({ settings: null, refresh: () => {} });

const FALLBACK = {
  brand_name: "Open Business Anywhere",
  legal_name: "Open Business Anywhere Inc.",
  tagline: "Start Your Business Anywhere. Expand Everywhere.",
  description: "A trusted partner helping entrepreneurs and businesses establish, operate, and expand internationally.",
  support_email: "hello@openbusinessanywhere.com",
  secondary_email: "",
  phone: "+1 (647) 555-0100",
  address_lines: ["123 Bay Street, Suite 2400", "Toronto, ON M5J 2T3", "Canada"],
  footer_text: "© 2020 Open Business Anywhere. All Rights Reserved.",
  meta_title: "Open Business Anywhere | International Business Setup & Expansion",
  meta_description: "Launch and grow your business globally.",
  logo_url: "",
  favicon_url: "",
  og_image_url: "",
  social: { twitter: "", linkedin: "", facebook: "", instagram: "", youtube: "" },
  established_year: 2020,
  terms_url: "", privacy_url: "",
};

export function SettingsProvider({ children }) {
  const [settings, setSettings] = useState(FALLBACK);

  const refresh = async () => {
    try {
      const { data } = await api.get("/settings");
      setSettings({ ...FALLBACK, ...data });
    } catch {
      setSettings(FALLBACK);
    }
  };

  useEffect(() => { refresh(); }, []);

  // Update document title & meta tags
  useEffect(() => {
    document.title = settings.meta_title || settings.brand_name;
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute("content", settings.meta_description || "");
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) ogTitle.setAttribute("content", settings.brand_name);
    const ogDesc = document.querySelector('meta[property="og:description"]');
    if (ogDesc) ogDesc.setAttribute("content", settings.meta_description || "");
    if (settings.og_image_url) {
      let ogImg = document.querySelector('meta[property="og:image"]');
      if (!ogImg) {
        ogImg = document.createElement("meta");
        ogImg.setAttribute("property", "og:image");
        document.head.appendChild(ogImg);
      }
      ogImg.setAttribute("content", settings.og_image_url);
    }
    if (settings.favicon_url) {
      const link = document.querySelector('link[rel="icon"]');
      if (link) {
        const url = settings.favicon_url.startsWith("http")
          ? settings.favicon_url
          : `${BACKEND_URL}${settings.favicon_url}?v=${Date.now()}`;
        link.setAttribute("href", url);
        link.removeAttribute("type");
      }
    }
  }, [settings]);

  return (
    <SettingsContext.Provider value={useMemo(() => ({ settings, refresh }), [settings])}>
      {children}
    </SettingsContext.Provider>
  );
}

export const useSettings = () => useContext(SettingsContext).settings;
export const useSettingsRefresh = () => useContext(SettingsContext).refresh;

export function logoUrl(settings) {
  if (!settings?.logo_url) return null;
  if (settings.logo_url.startsWith("http")) return settings.logo_url;
  return `${BACKEND_URL}${settings.logo_url}`;
}
